/**
 * US-95: self-service делегирование в профиле.
 *
 * Показывает под-секции по фактическим возможностям пользователя:
 *  - «Делегат по подчинённым» (ЛР) — пишет Employee.line_manager_delegate;
 *  - «Делегат по проектам» (PM) — пишет pm_delegate на ВСЕ проекты пользователя.
 * Каждая: пикер сотрудника (поиск среди активных, purpose=delegate) + период
 * «с»/«по» (опционально, пусто = бессрочно) + Сохранить/Снять.
 * Если делегировать нечего (can_delegate всё false) — секция не рендерится.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Group, Loader, Select, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";

import {
  fetchMyDelegations,
  searchDelegateCandidates,
  setLrDelegate,
  setPmDelegate,
  type DelegateRef,
  type DelegationInput,
  type MyDelegations,
} from "../api/client";

interface CurrentDelegation {
  delegate: DelegateRef | null;
  active_from: string | null;
  active_to: string | null;
}

interface BlockProps {
  title: string;
  hint: string;
  current: CurrentDelegation;
  onSave: (body: DelegationInput) => Promise<unknown>;
}

const toIso = (d: Date | null): string | null => (d ? dayjs(d).format("YYYY-MM-DD") : null);

function DelegateBlock({ title, hint, current, onSave }: BlockProps) {
  const [value, setValue] = useState<string | null>(
    current.delegate ? String(current.delegate.id) : null,
  );
  const [from, setFrom] = useState<Date | null>(
    current.active_from ? new Date(current.active_from) : null,
  );
  const [to, setTo] = useState<Date | null>(current.active_to ? new Date(current.active_to) : null);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>(
    current.delegate
      ? [{ value: String(current.delegate.id), label: current.delegate.full_name }]
      : [],
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Чистим debounce-таймер на unmount: иначе он стреляет после teardown
  // (в тестах — по сброшенному моку → unhandled rejection, флейк vitest;
  // в браузере — setState на размонтированном компоненте).
  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const handleSearch = useCallback((q: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 2) return;
    searchTimer.current = setTimeout(() => {
      searchDelegateCandidates(q.trim())
        .then((rows) => {
          setOptions((prev) => {
            const seen = new Set(prev.map((o) => o.value));
            const merged = [...prev];
            for (const r of rows) {
              if (!seen.has(String(r.id))) merged.push({ value: String(r.id), label: r.full_name });
            }
            return merged;
          });
        })
        .catch(() => {
          /* поиск тихо игнорируем */
        });
    }, 250);
  }, []);

  const save = useCallback(
    (clear: boolean) => {
      setSaving(true);
      setMsg(null);
      const body: DelegationInput = clear
        ? { delegate_id: null }
        : {
            delegate_id: value ? Number(value) : null,
            active_from: toIso(from),
            active_to: toIso(to),
          };
      onSave(body)
        .then(() => {
          if (clear) {
            setValue(null);
            setFrom(null);
            setTo(null);
          }
          setMsg({ ok: true, text: clear ? "Делегирование снято." : "Сохранено." });
        })
        .catch((e: Error) => setMsg({ ok: false, text: e.message || "Не удалось сохранить." }))
        .finally(() => setSaving(false));
    },
    [value, from, to, onSave],
  );

  return (
    <Stack gap={6}>
      <Text fw={500} size="sm">
        {title}
      </Text>
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
      <Select
        placeholder="Выберите сотрудника"
        searchable
        clearable
        data={options}
        value={value}
        onChange={setValue}
        onSearchChange={handleSearch}
        nothingFoundMessage="Начните вводить ФИО…"
        aria-label={title}
      />
      <Group grow gap="xs">
        <DatePickerInput
          label="С"
          value={from}
          onChange={setFrom as (v: Date | null) => void}
          valueFormat="DD.MM.YYYY"
          clearable
          placeholder="бессрочно"
        />
        <DatePickerInput
          label="По"
          value={to}
          onChange={setTo as (v: Date | null) => void}
          valueFormat="DD.MM.YYYY"
          clearable
          placeholder="бессрочно"
        />
      </Group>
      <Group gap="xs">
        <Button size="xs" onClick={() => save(false)} loading={saving} disabled={!value}>
          Сохранить
        </Button>
        {current.delegate && (
          <Button size="xs" variant="default" onClick={() => save(true)} disabled={saving}>
            Снять
          </Button>
        )}
      </Group>
      {msg && (
        <Text size="xs" c={msg.ok ? "teal" : "red"}>
          {msg.text}
        </Text>
      )}
    </Stack>
  );
}

export function DelegationSettings() {
  const [data, setData] = useState<MyDelegations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    fetchMyDelegations()
      .then(setData)
      .catch((e: Error) => setError(e.message || "Не удалось загрузить делегирование."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => reload(), [reload]);

  const pmCurrent = useMemo<CurrentDelegation>(() => {
    const first = data?.pm?.projects[0];
    return {
      delegate: first?.delegate ?? null,
      active_from: first?.active_from ?? null,
      active_to: first?.active_to ?? null,
    };
  }, [data]);

  if (loading) return <Loader size="sm" />;
  if (error) return <Alert color="red">{error}</Alert>;
  if (!data) return null;
  const { can_delegate } = data;
  if (!can_delegate.lr && !can_delegate.pm) return null;

  return (
    <Stack gap="lg" data-testid="delegation-settings">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        Делегирование
      </Text>
      {can_delegate.lr && data.lr && (
        <DelegateBlock
          title="Делегат по подчинённым"
          hint="Пока вас нет, заявки и таймшиты ваших подчинённых уйдут делегату."
          current={data.lr}
          onSave={(body) => setLrDelegate(body).then(reload)}
        />
      )}
      {can_delegate.pm && data.pm && (
        <DelegateBlock
          title="Делегат по проектам"
          hint={`Применится ко всем вашим проектам: ${data.pm.projects
            .map((p) => p.code)
            .join(", ")}`}
          current={pmCurrent}
          onSave={(body) => setPmDelegate(body).then(reload)}
        />
      )}
    </Stack>
  );
}
